/** Appended to an answer to let the copilot take its recommended choice for every remaining question. */
export const USE_DEFAULTS_LINE = 'Use your recommended defaults for the remaining questions.';

/** An option is `{label, description, recommended}`; a bare string (older cards) is a label with no explanation. */
function sanitizeOption(option) {
    const { label, description = '', recommended } = typeof option === 'string' ? { label: option } : (option || {});
    if (typeof label !== 'string' || !label.trim() || label.length > 200
        || typeof description !== 'string' || description.length > 200) return null;
    return { label: label.trim(), description: description.trim(), recommended: recommended === true };
}

/** Validate presentation data before rendering network/tool output. No options = free text only. */
export function sanitizeClarification(data) {
    if (!data || typeof data.id !== 'string' || !data.id || data.id.length > 100
        || typeof data.question !== 'string' || !data.question.trim() || data.question.length > 500
        || !Array.isArray(data.options) || data.options.length > 5) return null;
    const options = data.options.map(sanitizeOption);
    if (options.some(option => !option)) return null;
    if (new Set(options.map(option => option.label.toLowerCase())).size !== options.length) return null;
    // At most one recommendation: the card preselects it.
    const first = options.findIndex(option => option.recommended);
    options.forEach((option, index) => { option.recommended = index === first; });
    return { id: data.id, question: data.question.trim(), options, allowCustom: true };
}

/** True when the last reply is a question with no options: its answer is typed in the composer. */
export function awaitsTypedAnswer(messages) {
    const last = Array.isArray(messages) ? messages[messages.length - 1] : null;
    return Boolean(last?.clarification && !last.clarification.options.length);
}

/**
 * Put a locally cached pending card back on reloaded transcript rows.
 *
 * The runtime publishes the question as the turn's reply, so the card belongs on the last
 * row only when that row is the question. Anything else means the conversation moved on
 * (answered from another browser, say) and the cached card is stale.
 */
export function restoreClarification(rows, card) {
    const restored = Array.isArray(rows) ? [...rows] : [];
    const last = restored[restored.length - 1];
    if (card && last?.role === 'assistant' && String(last.content ?? '').trim() === card.question) {
        restored[restored.length - 1] = { ...last, clarification: card };
    }
    return restored;
}
